import React, {ChangeEvent, ReactElement, useEffect, useState} from 'react'
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import {useMutation} from '@apollo/react-hooks'
import {useHistory} from 'react-router-dom'
import {ADD_ACCOUNT_MUTATION} from '../../../graphql/mutations'
import {CurrentPromotion, PromotionOffer, PromotionTier} from './types'
import {
  PROMOTION_OPEN_MODAL_SUBTITLE,
  PROMOTION_OPEN_MODAL_TITLE,
  PROMOTION_RULES_ACCEPT_LABEL,
  promotionOpenOfferSummary,
  resolvePromotionOpenMultiplier,
} from './promotionOpenHelpers'
import './tenx-promotion.css'

type Props = {
  open: boolean
  onClose: () => void
  onOpenRules: () => void
  promotion: CurrentPromotion
  initialMultiplier: number
  initialTierName: string
}

export function PromotionOpenDialog({
  open,
  onClose,
  onOpenRules,
  promotion,
  initialMultiplier,
  initialTierName,
}: Props): ReactElement {
  const history = useHistory()
  const {campaign, offers} = promotion
  const [multiplier, setMultiplier] = useState(initialMultiplier)
  const [tierName, setTierName] = useState(initialTierName)
  const [accepted, setAccepted] = useState(false)
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const [createdId, setCreatedId] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setMultiplier(initialMultiplier)
    setTierName(initialTierName)
    setAccepted(false)
    setPassword('')
    setConfirm('')
    setFormError(null)
    setCreatedId(null)
  }, [open, initialMultiplier, initialTierName])

  const selectedOffer: PromotionOffer =
    offers.find((offer) => offer.multiplier === multiplier) ??
    offers.find((offer) => offer.canOpen) ??
    offers[0]
  const selectedTier: PromotionTier =
    selectedOffer.tiers.find((tier) => tier.name === tierName) ?? selectedOffer.tiers[0]
  const openableOffers = offers.filter((offer) => offer.canOpen)
  const resolved = resolvePromotionOpenMultiplier(offers, selectedOffer.multiplier)
  const canSubmit =
    resolved != null && selectedOffer.canOpen && accepted && password.trim().length > 0 && password === confirm

  const [createAccount, {loading}] = useMutation(ADD_ACCOUNT_MUTATION)

  const onSubmit = async (): Promise<void> => {
    setFormError(null)
    if (resolved == null) {
      setFormError('This Factor is not available for your account.')
      return
    }
    if (!accepted) {
      setFormError('Accept the Tauro 10x Account Rules to continue.')
      return
    }
    if (!password.trim()) {
      setFormError('Enter your Tauro Markets profile password.')
      return
    }
    if (password !== confirm) {
      setFormError('Passwords must match.')
      return
    }
    try {
      const result = await createAccount({
        variables: {
          password,
          currency: campaign.rules.promotion.currency,
          accountType: 'ForexMT5SVGTauromarketsAccount',
          accountSubtype: 'mt5Std',
          tauroTermsAccepted: true,
          tauroFactorisationRequested: true,
          tauroMultiplier: resolved,
          promotionCampaignId: campaign.id,
          promotionTier: selectedTier.name,
        },
      })
      const id = result.data?.account?.id
      setCreatedId(id != null ? String(id) : 'created')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not open the account. Try again.'
      setFormError(message.replace('GraphQL error: ', ''))
    }
  }

  if (createdId) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-testid="pm-open-dialog">
        <DialogTitle>Your Factorised Account is ready</DialogTitle>
        <DialogContent>
          <Typography paragraph>Deposit now to fund the account with the tier you chose.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
          <Button
            className="pm-btn"
            variant="contained"
            disableElevation
            onClick={() => {
              onClose()
              history.push('/transactions')
            }}
          >
            Deposit →
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-testid="pm-open-dialog">
      <DialogTitle disableTypography>
        <Typography variant="h6" component="h2">
          {PROMOTION_OPEN_MODAL_TITLE}
        </Typography>
        <IconButton aria-label="Close" onClick={onClose} style={{position: 'absolute', right: 8, top: 8}}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography color="textSecondary" paragraph>
          {PROMOTION_OPEN_MODAL_SUBTITLE}
        </Typography>
        <FormControl fullWidth margin="normal">
          <InputLabel id="pm-open-factor-label">Factor</InputLabel>
          <Select
            labelId="pm-open-factor-label"
            value={selectedOffer.multiplier}
            onChange={(event: ChangeEvent<{value: unknown}>) => {
              const next = Number(event.target.value)
              const offer = offers.find((item) => item.multiplier === next)!
              setMultiplier(next)
              setTierName(offer.tiers[0]?.name ?? '')
            }}
          >
            {openableOffers.map((offer) => (
              <MenuItem key={offer.multiplier} value={offer.multiplier}>
                {offer.multiplier}X
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth margin="normal">
          <InputLabel id="pm-open-tier-label">Tier</InputLabel>
          <Select
            labelId="pm-open-tier-label"
            value={selectedTier.name}
            onChange={(event: ChangeEvent<{value: unknown}>) => setTierName(String(event.target.value))}
          >
            {selectedOffer.tiers.map((tier) => (
              <MenuItem key={tier.name} value={tier.name}>
                {tier.name} · ${tier.deposit.toLocaleString()}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" paragraph style={{marginTop: 12}}>
          {promotionOpenOfferSummary({
            offer: selectedOffer,
            tier: selectedTier,
            currency: campaign.rules.promotion.currency,
          })}
        </Typography>
        <Button color="primary" onClick={onOpenRules} style={{paddingLeft: 0}}>
          Read the rules
        </Button>
        <TextField
          fullWidth
          margin="normal"
          type="password"
          label="Profile password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          inputProps={{'data-testid': 'pm-open-password'}}
        />
        <TextField
          fullWidth
          margin="normal"
          type="password"
          label="Confirm password"
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          inputProps={{'data-testid': 'pm-open-confirm'}}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={accepted}
              onChange={(_, checked) => setAccepted(checked)}
              color="primary"
              inputProps={{'data-testid': 'pm-open-accept'} as React.InputHTMLAttributes<HTMLInputElement>}
            />
          }
          label={PROMOTION_RULES_ACCEPT_LABEL}
        />
        {formError && (
          <Typography color="error" role="alert">
            {formError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          className="pm-btn"
          variant="contained"
          disableElevation
          disabled={!canSubmit || loading}
          onClick={() => {
            void onSubmit()
          }}
          data-testid="pm-open-submit"
        >
          {loading ? 'Opening…' : 'Open account'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
